import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import PublicLayout from "@/components/layout/PublicLayout";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { iconFor } from "@/lib/amenities";

const FILTERS = [
  { key: "pet_friendly", label: "Animali ammessi", icon: "PawPrint" },
  { key: "parcheggio", label: "Parcheggio", icon: "Car" },
  { key: "wifi", label: "Wi-Fi", icon: "Wifi" },
];

export default function CaseVacanze() {
  const [params, setParams] = useSearchParams();
  const { t } = useLang();
  const [properties, setProperties] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const city = params.get("city") || "";
  const guests = params.get("guests") || "";
  const selAmenities = (params.get("amenities") || "").split(",").filter(Boolean);

  useEffect(() => {
    api.get("/public/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (city) q.city = city;
    if (guests) q.guests = guests;
    if (selAmenities.length) q.amenities = selAmenities.join(",");
    api.get("/public/properties", { params: q })
      .then((r) => setProperties(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const toggleAmenity = (key) => {
    const set = new Set(selAmenities);
    set.has(key) ? set.delete(key) : set.add(key);
    update("amenities", Array.from(set).join(","));
  };

  return (
    <PublicLayout>
      <section className="bg-brand-plum text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-3">{t("listing.kicker")}</p>
          <h1 className="text-4xl sm:text-5xl font-serif">{t("listing.title")}</h1>
          <p className="mt-4 text-white/75 max-w-2xl">{t("listing.desc")}</p>
        </div>
      </section>

      {/* Filters bar */}
      <div className="sticky top-16 md:top-20 z-30 bg-brand-canvas/95 backdrop-blur border-b border-brand-plum/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <Select value={city || "all"} onValueChange={(v) => update("city", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-city-select" className="w-[180px] bg-white rounded-full border-brand-plum/15">
              <SelectValue placeholder="Città" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("listing.allCities")}</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="hidden md:flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => {
              const Icon = iconFor(f.icon);
              const active = selAmenities.includes(f.key);
              return (
                <button
                  key={f.key}
                  onClick={() => toggleAmenity(f.key)}
                  data-testid={`filter-amenity-${f.key}`}
                  className={`flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full border transition-all ${
                    active ? "bg-brand-teal border-brand-teal text-brand-violet font-medium" : "bg-white border-brand-plum/15 text-brand-plum/70 hover:border-brand-teal"
                  }`}
                >
                  <Icon size={14} /> {f.label}
                </button>
              );
            })}
          </div>

          <button
            className="md:hidden flex items-center gap-2 text-sm px-4 py-1.5 rounded-full border border-brand-plum/15 bg-white text-brand-plum"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="mobile-filters-toggle"
          >
            <SlidersHorizontal size={15} /> Servizi
          </button>
        </div>

        {showFilters && (
          <div className="md:hidden border-t border-brand-plum/10 bg-white px-4 py-4 flex flex-wrap gap-2">
            {FILTERS.map((f) => {
              const active = selAmenities.includes(f.key);
              return (
                <button
                  key={f.key}
                  onClick={() => toggleAmenity(f.key)}
                  data-testid={`filter-amenity-${f.key}-mobile`}
                  className={`text-sm px-3.5 py-1.5 rounded-full border ${active ? "bg-brand-teal border-brand-teal text-brand-violet" : "bg-white border-brand-plum/15 text-brand-plum/70"}`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-brand-plum/60" data-testid="results-count">
            {loading ? t("listing.loading") : `${properties.length} ${t("listing.found")}`}
            {guests && <span data-testid="results-guests"> · {t("listing.forGuests").replace("{n}", guests)}</span>}
          </p>
          {(city || guests || selAmenities.length > 0) && (
            <button
              onClick={() => setParams(new URLSearchParams())}
              data-testid="clear-filters-btn"
              className="flex items-center gap-1 text-sm text-brand-teal hover:underline"
            >
              <X size={14} /> {t("listing.clear")}
            </button>
          )}
        </div>

        {!loading && properties.length === 0 ? (
          <div className="text-center py-24 text-brand-plum/50">
            <p className="font-serif text-2xl text-brand-plum mb-2">{t("listing.noneTitle")}</p>
            <p>{t("listing.noneDesc")}</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {properties.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
